import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  effect,
  OnDestroy,
  ApplicationRef,
  Injector,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { Location, UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import * as Highcharts from 'highcharts';
import { Chart } from 'highcharts';
import { HighchartsChartDirective, HighchartsChartComponent } from 'highcharts-angular';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';

import { DATE_COLORS } from './chart-colors.constants';
import { CowTooltipComponent } from './components/cow-tooltip.component';
import { TooltipData } from './models/tooltip.models';
import {
  SelectedDateWithColor,
  CompanyReportPrintComponent,
} from './print-view/company-report-print.component';
import { MeasurementsGraphDataFacade } from './services/measurements-graph-data.facade';
import { MeasurementsGraphSeriesFactory } from './services/measurements-graph-series.factory';
import { AlvaMetricsDto } from '../../../insemination-advice/models/alva-metrics.model';
import {
  InseminationAdviceDto,
  InseminationAdviceStatus,
  InseminationPrintRow,
  sortInseminationAdvice,
} from '../../../insemination-advice/models/insemination-advice.model';
import { InseminationAdviceService } from '../../../insemination-advice/services/insemination-advice.service';
import { CowWithMeasurement } from '../../models/cow.model';
import { GraphDataDto } from '../../models/measurement.model';
import { CowService } from '../../services/cow.service';

const DEFAULT_MINIMUM_HEIGHT_WITH_MEASUREMENT = 135;

@Component({
  selector: 'measurements-graph',
  templateUrl: './measurements-graph.component.html',
  styleUrls: ['./measurements-graph.component.scss'],
  providers: [MeasurementsGraphDataFacade, MeasurementsGraphSeriesFactory],
  imports: [
    MatButton,
    MatIcon,
    MatCheckbox,
    HighchartsChartDirective,
    HighchartsChartComponent,
    CompanyReportPrintComponent,
    DatePipe,
    TranslatePipe,
  ],
})
export class MeasurementsGraphComponent implements OnInit, OnDestroy {
  private readonly appRef = inject(ApplicationRef);
  protected readonly cowService: CowService = inject(CowService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly graphDataFacade = inject(MeasurementsGraphDataFacade);
  private readonly graphSeriesFactory = inject(MeasurementsGraphSeriesFactory);
  private readonly injector = inject(Injector);
  private readonly inseminationService = inject(InseminationAdviceService);
  private readonly settingsService = inject(SettingsService);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public readonly chartOptions = signal<Highcharts.Options>({});
  protected readonly localeService = inject(LocaleService);
  public readonly availableDates = signal<Date[]>([]);
  public readonly selectedDates = signal<Date[]>([]);
  public readonly selectedCowId = signal<string | null>(null);
  public readonly isIndividualMonitoring = computed(() => this.selectedCowId() !== null);
  private readonly chartInstance = signal<Chart | null>(null);
  protected readonly translatePipe: TranslatePipe = inject(TranslatePipe);
  private readonly isLoading = signal<boolean>(true);
  private tooltipOutlet?: DomPortalOutlet;
  private readonly tooltipData = signal<TooltipData | null>(null);
  private readonly tooltipRendered = signal<boolean>(false);
  public readonly maxSelections = 3;
  private readonly currentlySelectedPoint = signal<Highcharts.Point | null>(null);
  private readonly currentGraphData = signal<GraphDataDto | null>(null);

  // Print-related properties
  protected chartImageForPrint: string | null = null;
  protected printTableData: InseminationPrintRow[] = [];
  protected reportDateForPrint: Date | null = null;
  protected alvaMetricsForPrint: AlvaMetricsDto | null = null;
  protected selectedDatesForPrint: SelectedDateWithColor[] = [];
  protected locationForPrint: Location | null = null;
  protected stallListCowMeasurementsForPrint: CowWithMeasurement[] = [];
  protected stallListDateForPrint: Date | null = null;
  private minimumHeightForPrint = DEFAULT_MINIMUM_HEIGHT_WITH_MEASUREMENT;
  private readonly location$ = this.store.pipe(select(UserSelectors.selectLocation));

  constructor() {
    effect(() => {
      const chart = this.chartInstance();
      if (!chart) {
        return;
      }

      if (this.selectedDates().length === 0) {
        this.clearChart();
        return;
      }

      // Read selectedCowId to refresh the graph when switching between company and individual monitoring.
      this.loadChartData();
    });
  }

  ngOnInit() {
    this.setupChart();
    this.route.paramMap.subscribe((paramMap) => {
      this.selectedCowId.set(paramMap.get('cowId'));
    });

    this.cowService.getMeasurementDates().subscribe((dates) => {
      this.availableDates.set(dates);
      if (dates.length > 0) {
        this.selectedDates.set([dates[0]]);
      }
    });

    this.localeService.locale$.subscribe(() => {
      this.updateChartLabels();
    });
  }

  ngOnDestroy() {
    this.closeTooltip();
    this.chartInstance.set(null);
  }

  public isDateSelected(date: Date): boolean {
    return this.selectedDates().includes(date);
  }

  public canSelectMore(): boolean {
    return this.selectedDates().length < this.maxSelections;
  }

  public toggleDate(date: Date): void {
    const selected = this.selectedDates();
    const isSelected = this.isDateSelected(date);

    if (isSelected) {
      this.selectedDates.set(selected.filter((d) => d !== date));
    } else if (this.canSelectMore()) {
      this.selectedDates.set([...selected, date]);
    }
  }

  /**
   * Get color for a specific date based on its index in selectedDates.
   */
  public getDateColor(date: Date): string {
    const index = this.selectedDates().indexOf(date);
    return index >= 0 ? DATE_COLORS[index].primary : DATE_COLORS[0].primary;
  }

  private loadChartData(): void {
    const cowId = this.selectedCowId();
    const graphData$ = cowId
      ? this.graphDataFacade.loadSingleCowGraphData(cowId, this.availableDates())
      : this.graphDataFacade.loadCompanyGraphData(this.selectedDates());

    graphData$.subscribe((data) => {
      if (data) {
        this.currentGraphData.set(data);
        this.updateChartWithDateGroupedData(data);
      }
      this.isLoading.set(false);
    });
  }

  private clearChart(): void {
    const chart = this.chartInstance();
    if (!chart) return;

    while (chart.series.length > 0) {
      chart.series.at(-1).remove(false);
    }
    chart.redraw();
  }

  private updateChartWithDateGroupedData(data: GraphDataDto): void {
    const chart = this.chartInstance();
    if (!chart) {
      return;
    }

    if (!data?.dataByDate || !Array.isArray(data.dataByDate)) {
      return;
    }

    while (chart.series.length > 0) {
      chart.series.at(-1)?.remove(false);
    }

    this.graphSeriesFactory
      .createSeries(data, {
        isIndividualMonitoring: this.isIndividualMonitoring(),
        translatePipe: this.translatePipe,
      })
      .forEach((seriesConfig) => this.addGraphElement(chart, seriesConfig));

    chart.redraw();
  }

  private addGraphElement(chart: Chart, seriesConfig: Highcharts.SeriesOptionsType): void {
    chart.addSeries(seriesConfig, false);
  }

  protected setupChartInstance($event: Chart) {
    this.chartInstance.set($event);
  }

  private closeTooltip(): void {
    this.currentlySelectedPoint.set(null);
    this.chartInstance()?.tooltip?.hide(0);
    if (this.tooltipOutlet) {
      this.tooltipOutlet.detach();
      this.tooltipRendered.set(false);
    }
  }

  private setupChart(): void {
    const component = this;
    this.chartOptions.update(() => ({
      palette: {
        colorScheme: 'light',
      },
      title: {
        text: `${this.translatePipe.transform('height-measurement.monitoring.graph.title')}`,
      },
      credits: {
        text: `${this.translatePipe.transform('height-measurement.monitoring.graph.credits')}`,
      },
      xAxis: {
        title: {
          text: `${this.translatePipe.transform('height-measurement.monitoring.graph.xAxisTitle')}`,
        },
      },
      yAxis: {
        title: {
          text: `${this.translatePipe.transform('height-measurement.monitoring.graph.yAxisTitle')}`,
        },
      },
      tooltip: {
        className: 'cow-monitoring-tooltip',
        headerFormat: '<div id="tooltip-host"></div>',
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderRadius: 0,
        shadow: false,
        formatter: function (
          this: Highcharts.Point & {
            cowId?: string;
            earNumber?: string;
            registrationNumber?: string;
            bodyConditionScore?: number;
            isExcludedFromMeasurements: boolean;
          },
        ) {
          if (component.currentlySelectedPoint() !== this) {
            return false;
          }

          component.tooltipData.set({
            cowId: this.cowId || '',
            name: this.name || '',
            earNumber: this.earNumber || '',
            age: this.x || 0,
            height: this.y || 0,
            registrationNumber: this.registrationNumber || '',
            bodyConditionScore: this.bodyConditionScore || 0,
            isExcludedFromMeasurements: this.isExcludedFromMeasurements,
          });

          return '<div id="tooltip-host"></div>';
        },
        useHTML: true,
        outside: false,
        hideDelay: 300,
        stickOnContact: true,
        style: {
          pointerEvents: 'auto',
          opacity: 1,
        },
      },
      plotOptions: {
        series: {
          enableMouseTracking: true,
          stickyTracking: false,
          point: {
            events: {
              click: (event: Highcharts.PointClickEventObject) => {
                this.openTooltip(event.point);
              },
              mouseOver: () => {
                return false;
              },
            },
          },
        },
      },
      series: [],
    }));
  }

  private updateChartLabels(): void {
    if (this.chartInstance() && this.selectedDates().length > 0) {
      this.loadChartData();
    }
  }

  private openTooltip(point: Highcharts.Point): void {
    this.closeTooltip();
    this.currentlySelectedPoint.set(point);
    const chart = this.chartInstance();
    if (!chart) {
      return;
    }

    chart.tooltip.refresh(point);
    this.attachTooltip();
  }

  private attachTooltip(): void {
    const point = this.currentlySelectedPoint();
    if (this.tooltipRendered() || point === null) {
      return;
    }

    const host = document.querySelector('#tooltip-host');
    if (!host) {
      return;
    }

    this.tooltipOutlet?.detach();
    this.tooltipOutlet = new DomPortalOutlet(host, this.appRef, this.injector);

    const cowComponentPortal = new ComponentPortal(CowTooltipComponent, null, this.injector);
    const componentRef = this.tooltipOutlet.attach(cowComponentPortal);

    const tooltipDataValue = this.tooltipData();
    if (componentRef.instance && tooltipDataValue) {
      componentRef.instance.data.set(tooltipDataValue);
      componentRef.instance.localizedStrings.set({
        name: this.translatePipe.transform('height-measurement.table.name'),
        earNumber: this.translatePipe.transform('height-measurement.table.earNumber'),
        age: this.translatePipe.transform('height-measurement.table.ageInMonths'),
        height: this.translatePipe.transform('height-measurement.table.hipHeight'),
        bcs: this.translatePipe.transform('height-measurement.table.bcs'),
        excludeButton: this.translatePipe.transform('height-measurement.buttons.exclude'),
        includeButton: this.translatePipe.transform('height-measurement.buttons.include'),
        individualMonitoringButton: this.translatePipe.transform(
          'height-measurement.monitoring.buttons.individual-monitoring',
        ),
      });
      componentRef.instance.showIndividualMonitoringButton.set(!this.isIndividualMonitoring());
      componentRef.instance.showExcludeFromAverageControl.set(!this.isIndividualMonitoring());

      componentRef.instance.exclude.subscribe((exclude) =>
        this.handleExcludeCow(exclude.registrationNumber, exclude.isExcluded),
      );
      componentRef.instance.viewIndividualMonitoring.subscribe((cowId) =>
        this.navigateToIndividualMonitoring(cowId),
      );
      componentRef.instance.closeTooltip.subscribe(() => this.closeTooltip());

      componentRef.changeDetectorRef.detectChanges();
    }
    this.tooltipRendered.set(true);
    this.chartInstance()?.tooltip.refresh(point);
  }

  private handleExcludeCow(registrationNumber: string, isExcluded: boolean): void {
    this.cowService.excludeCowFromMeasurements(registrationNumber, isExcluded).subscribe((res) => {
      const failedToExclude = res.status !== 200;
      if (failedToExclude) {
        const message = this.translatePipe.transform('height-measurement.messages.saveFailed');
        this.store.dispatch(
          NotificationActions.errorSnackbar({
            message,
          }),
        );
      } else {
        const message = this.translatePipe.transform('height-measurement.messages.saveSuccess');
        this.store.dispatch(
          NotificationActions.successSnackbar({
            message,
          }),
        );
      }
      this.loadChartData();
    });
    this.closeTooltip();
  }

  protected navigateToIndividualMonitoring(cowId: string): void {
    this.closeTooltip();
    setTimeout(() => {
      void this.router.navigate(['/monitoring/cow', cowId]);
    }, 0);
  }

  protected navigateToCompanyMonitoring(): void {
    void this.router.navigate(['/monitoring']);
  }

  protected async printCompanyReport(): Promise<void> {
    const chart = this.chartInstance();
    if (!chart) {
      return;
    }

    const svgElement = chart.container.querySelector('svg');
    if (!svgElement) {
      console.error('No SVG element found in chart container');
      return;
    }

    const clonedSvg = svgElement.cloneNode(true) as SVGElement;
    clonedSvg.setAttribute('width', '900');
    clonedSvg.setAttribute('height', '500');
    // The palette CSS in the SVG targets this attribute on the live chart container.
    clonedSvg.dataset.highchartsChart = String(chart.index);
    clonedSvg.style.colorScheme = 'light';

    const svgString = new XMLSerializer().serializeToString(clonedSvg);
    this.chartImageForPrint = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
    this.selectedDatesForPrint = this.selectedDates().map((date, index) => ({
      date,
      color: DATE_COLORS[index].primary,
    }));

    this.locationForPrint = await firstValueFrom(this.location$);

    const thresholds = await firstValueFrom(
      this.settingsService
        .getSingle<{ minimumHeightWithMeasurement: number }>(
          {
            contextName: 'DairyStartMonitor',
            key: 'insemination-advice-thresholds',
            storage: SettingsStorage.Global,
            basedOnCurrentLocation: true,
          },
          { minimumHeightWithMeasurement: DEFAULT_MINIMUM_HEIGHT_WITH_MEASUREMENT },
        )
        .pipe(
          catchError(() =>
            of({ minimumHeightWithMeasurement: DEFAULT_MINIMUM_HEIGHT_WITH_MEASUREMENT }),
          ),
        ),
    );
    this.minimumHeightForPrint = thresholds.minimumHeightWithMeasurement;

    // Fetch insemination data, ALVA metrics, and latest stall list
    const rawLatestDate = this.availableDates()[0] ?? null;
    const latestDate = rawLatestDate ? new Date(rawLatestDate) : null;
    forkJoin({
      advice: this.inseminationService.getInseminationAdvice().pipe(
        catchError((error) => {
          console.error('Failed to load insemination advice for print:', error);
          return of([] as InseminationAdviceDto[]);
        }),
      ),
      alva: this.inseminationService.getAlvaMetrics(this.minimumHeightForPrint).pipe(
        catchError((error) => {
          console.error('Failed to load ALVA metrics for print:', error);
          return of(null as AlvaMetricsDto | null);
        }),
      ),
      stallList: latestDate
        ? this.cowService
            .getCowsForMeasurement({ measurementDate: latestDate, allowEmptyMeasurements: true })
            .pipe(
              catchError((error) => {
                console.error('Failed to load stall list for print:', error);
                return of([] as CowWithMeasurement[]);
              }),
            )
        : of([] as CowWithMeasurement[]),
    }).subscribe(({ advice, alva, stallList }) => {
      const sortedAdvice = sortInseminationAdvice(advice);
      this.printTableData = this.mapAdviceToPrintRows(sortedAdvice);
      this.alvaMetricsForPrint = alva;
      this.reportDateForPrint = new Date();
      this.stallListCowMeasurementsForPrint = stallList;
      this.stallListDateForPrint = latestDate;

      this.cdr.detectChanges();

      const originalTitle = document.title;
      const date = new Date().toLocaleDateString('nl-NL').split('/').join('-');
      const reportTitle = this.translatePipe.transform(
        'height-measurement.monitoring.print.header-title',
      );
      document.title = `${reportTitle}_${date}`;

      const cleanupAfterPrint = () => {
        document.title = originalTitle;
        this.chartImageForPrint = null;
        this.printTableData = [];
        this.reportDateForPrint = null;
        this.alvaMetricsForPrint = null;
        this.selectedDatesForPrint = [];
        this.locationForPrint = null;
        this.stallListCowMeasurementsForPrint = [];
        this.stallListDateForPrint = null;
        this.cdr.detectChanges();

        globalThis.removeEventListener('afterprint', cleanupAfterPrint);
      };

      globalThis.addEventListener('afterprint', cleanupAfterPrint);

      setTimeout(() => {
        globalThis.print();
      }, 50);
    });
  }

  private mapAdviceToPrintRows(advice: InseminationAdviceDto[]): InseminationPrintRow[] {
    return advice.map((r) => {
      let className = '';
      switch (r.status) {
        case InseminationAdviceStatus.InseminateAsap:
          className = 'row-inseminate-asap';
          break;
        case InseminationAdviceStatus.Inseminate:
          className = 'row-inseminate';
          break;
        case InseminationAdviceStatus.AlreadyInseminated:
          className = 'row-already-inseminated';
          break;
      }

      return {
        number: r.registrationNumber,
        earNumber: r.earNumber,
        name: r.name,
        ageInMonths: r.ageInMonths,
        hipHeight: r.heightCm,
        estimatedHipHeight: r.estimatedHeightCm,
        lastInsemination: r.dateOfLastInsemination ? new Date(r.dateOfLastInsemination) : null,
        advice: this.getAdviceText(r),
        status: r.status,
        className,
      };
    });
  }

  private getAdviceText(cow: InseminationAdviceDto): string {
    switch (cow.status) {
      case InseminationAdviceStatus.InseminateAsap:
        return this.translatePipe.transform('insemination-advice.status.inseminate-asap');
      case InseminationAdviceStatus.Inseminate:
        return this.translatePipe.transform('insemination-advice.status.inseminate');
      case InseminationAdviceStatus.InseminateInXDays:
        if (cow.predictedInseminationDate) {
          const date = new Date(cow.predictedInseminationDate);
          const formattedDate = date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          const template = this.translatePipe.transform(
            'insemination-advice.status.inseminate-in-days',
          );
          return template.replace('{{date}}', formattedDate);
        }
        return '';
      case InseminationAdviceStatus.AlreadyInseminated:
        if (cow.dateOfLastInsemination) {
          const date = new Date(cow.dateOfLastInsemination);
          const formattedDate = date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          const template = this.translatePipe.transform(
            'insemination-advice.status.already-inseminated',
          );
          return template.replace('{{date}}', formattedDate);
        }
        return this.translatePipe.transform('insemination-advice.status.already-inseminated');
      default:
        return '';
    }
  }
}
